import { Helmet } from "react-helmet-async";
import { PLANS } from "@/data/landing";
import { blogPostPath, type BlogPostMeta } from "@/data/blog-posts";
import { SITE } from "@/config/site.js";

/**
 * Renders a schema.org JSON-LD block into <head> via react-helmet-async.
 * Keep the payloads minimal and valid — test with
 * https://search.google.com/test/rich-results
 */
const JsonLd = ({ data }: { data: Record<string, unknown> }) => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(data)}</script>
  </Helmet>
);

export default JsonLd;

const PLAN_NAMES: Record<string, string> = {
  starter: "Starter",
  professional: "Professional",
  business: "Business",
};

/**
 * SoftwareApplication schema for Workloop. Offers are derived from PLANS in
 * src/data/landing.ts (single source of truth) so structured data never
 * drifts from the marketing pricing.
 */
export function softwareApplicationSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Workloop",
    url: `${SITE}/`,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    description:
      "Field service management software for HVAC, plumbing, and electrical businesses. Scheduling, dispatching, invoicing, real-time GPS tracking, and a customer-facing mobile app. Fully bilingual (English and Spanish).",
    inLanguage: ["en", "es"],
    offers: PLANS.map((plan) => ({
      "@type": "Offer",
      name: `${PLAN_NAMES[plan.id]} plan`,
      price: plan.price_monthly,
      priceCurrency: "USD",
      url: `${SITE}/pricing`,
      category: "subscription",
    })),
  };
}

export interface FaqEntry {
  question: string;
  answer: string;
}

/** FAQPage schema — pass the already-translated question/answer strings. */
export function faqPageSchema(items: FaqEntry[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answer,
      },
    })),
  };
}

// Stable entity ids so every schema block on the site references the same
// Organization / Person nodes instead of redefining them.
export const ORGANIZATION_ID = `${SITE}/#organization`;
export const AUTHOR_PERSON_ID = `${SITE}/authors/cristian-machado#person`;

const organizationRef = {
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: "Workloop",
  url: `${SITE}/`,
};

/**
 * Person schema for the author entity page (/authors/cristian-machado).
 * `sameAs` is the highest-leverage field — entity-confidence systems (and AI
 * answer engines) traverse it to confirm the author is a real person.
 */
export function personSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": AUTHOR_PERSON_ID,
    name: "Cristian Machado",
    url: `${SITE}/authors/cristian-machado`,
    jobTitle: "Founder",
    worksFor: organizationRef,
    description:
      "Founder of Workloop. Based in Miami, building bilingual (English/Spanish) field service software for HVAC, plumbing, and electrical businesses.",
    knowsAbout: [
      "field service management",
      "HVAC business operations",
      "software para negocios de servicios",
    ],
    knowsLanguage: ["en", "es"],
    sameAs: ["https://www.linkedin.com/in/cristianmachado"],
  };
}

/** BlogPosting schema for the blog post template. */
export function blogPostingSchema(post: BlogPostMeta): Record<string, unknown> {
  const url = `${SITE}${blogPostPath(post)}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    inLanguage: post.language,
    datePublished: post.date,
    dateModified: post.updated ?? post.date,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    author: { "@type": "Organization", name: post.author, url: `${SITE}/` },
    // Every post is human-reviewed; the editor node points at the author
    // entity page so crawlers can resolve the person behind the review.
    editor: { "@type": "Person", "@id": AUTHOR_PERSON_ID, name: "Cristian Machado" },
    publisher: organizationRef,
  };
}
